import { regionName } from "./countries";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatCustomerSince(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${day} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, ${time}`;
}

/** Calendar date in UTC, no clock time — used on the customers list. */
export function formatDateUtc(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${day} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

export function formatFxStatus(fx: {
  as_of?: string | null;
  updated_at?: string | null;
  source?: string | null;
} | null | undefined): string {
  if (!fx) return "Rates have not been loaded yet.";
  const rateDate = fx.as_of ? formatDateUtc(fx.as_of) : null;
  const refreshed = fx.updated_at ? formatUtc(fx.updated_at) : "never";
  const source = fx.source || "not set";
  const ratePart = rateDate ? `Rate date ${rateDate}. ` : "";
  return `${ratePart}Last refreshed ${refreshed} · source ${source}`;
}

/** Last-login timestamps are stored and shown in UTC, never local wall time. */
export function formatUtc(iso: string | null | undefined): string {
  if (!iso) return "Never";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Never";
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hh = String(date.getUTCHours()).padStart(2, "0");
  const mm = String(date.getUTCMinutes()).padStart(2, "0");
  return `${day} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}, ${hh}:${mm} UTC`;
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return remMin ? `${hours}h ${remMin}m` : `${hours}h`;
}

/** Platform AI spend. Amounts under one cent keep enough digits to stay visible. */
export function formatAiUsd(amount: number | null | undefined): string {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return "$0.00";
  if (value >= 0.01) return `$${value.toFixed(2)}`;
  return `$${value.toFixed(6).replace(/0+$/, "").replace(/\.$/, "")}`;
}

export function money(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
}

export function compactUsd(cents: number): string {
  const dollars = cents / 100;
  const abs = Math.abs(dollars);
  if (abs >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (abs >= 1000) return `$${(dollars / 1000).toFixed(1)}k`;
  return money(cents, "USD");
}

export function formatBytes(bytes: number | undefined): string {
  if (bytes == null) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  const shown = value < 10 && index > 0 ? value.toFixed(1) : String(Math.round(value));
  return `${shown} ${units[index]}`;
}

export function formatUptime(seconds: number | undefined): string {
  if (!seconds) return "";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h up`;
  if (hours > 0) return `${hours}h up`;
  return `${Math.floor(seconds / 60)}m up`;
}

const COUNTRY_NAME: Record<string, string> = {
  AU: "Australia",
  US: "United States",
  GB: "United Kingdom",
  UK: "United Kingdom",
  ZA: "South Africa",
  CA: "Canada",
  NZ: "New Zealand",
  IE: "Ireland",
  DE: "Germany",
  FR: "France",
  IN: "India",
  SG: "Singapore",
  CN: "China",
  RU: "Russia",
  NL: "Netherlands",
  PH: "Philippines",
  BE: "Belgium",
  AT: "Austria",
  ES: "Spain",
  IT: "Italy",
  PT: "Portugal",
  FI: "Finland",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  CH: "Switzerland",
  JP: "Japan",
  HK: "Hong Kong",
  MY: "Malaysia",
  AE: "United Arab Emirates",
  SA: "Saudi Arabia",
  BR: "Brazil",
  MX: "Mexico",
};

export function countryLabel(code: string | null | undefined): string {
  if (!code || code === "(unknown)") return "Unknown";
  const name = COUNTRY_NAME[code] ?? regionName(code);
  return name ? `${name} (${code})` : code;
}

function isPrivateIp(ip: string): boolean {
  const parts = ip.split(".").map((part) => Number(part));
  if (parts.length !== 4 || parts.some((part) => !Number.isInteger(part) || part < 0 || part > 255)) {
    return ip === "::1" || ip.startsWith("fe80:") || ip.startsWith("fc") || ip.startsWith("fd");
  }
  const [a, b] = parts;
  return a === 10 || a === 127 || (a === 192 && b === 168) || (a === 172 && b >= 16 && b <= 31);
}

export function formatIp(ip: string | null | undefined): string {
  if (!ip) return "—";
  return isPrivateIp(ip) ? `${ip} (proxy)` : ip;
}

